import React from 'react';
import { tv } from 'tailwind-variants';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import type { Section, StepConfig, FormInstance } from '../../types';
import type { SectionStatus } from '../../utils/sectionStatus';
import { getFormInstanceSummary } from '../../utils/formSummary';
import { SectionHeader } from './SectionHeader';

interface LegacySectionProps {
  section: Section;
  sectionIndex: number;
  steps: StepConfig[];
  completedInstances: FormInstance[];
  isCTAEnabled: boolean;
  sectionStatus: SectionStatus;
  onSectionClick: (sectionId: string) => void;
  onSectionEdit: (sectionId: string, instanceId: string) => void;
}

export const LegacySection: React.FC<LegacySectionProps> = ({
  section,
  steps,
  completedInstances,
  isCTAEnabled,
  sectionStatus,
  onSectionClick,
  onSectionEdit,
}) => {
  const {
    container,
    cardsContainer,
    cardContainer,
    cardHeader,
    editButton,
    summaryContainer,
    summaryItem,
    summaryLabel,
    summaryValue,
    ctaContainer,
  } = TAILWIND_VARIANTS();

  return (
    <div key={section.id} className={container()}>
      <SectionHeader title={section.title} status={sectionStatus} />

      {/* Render completed form cards */}
      {completedInstances.length > 0 && (
        <div className={cardsContainer()}>
          {completedInstances.map((instance) => {
            const summary = getFormInstanceSummary(steps, instance.data);
            return (
              <div key={instance.instanceId} className={cardContainer()}>
                <div className={cardHeader()}>
                  <button
                    type="button"
                    onClick={() => onSectionEdit(section.id, instance.instanceId)}
                    className={editButton()}
                    aria-label={'Edit'}
                  >
                    Edit
                  </button>
                </div>
                <div className={summaryContainer()}>
                  {summary.map((item, idx) => (
                    <div key={idx} className={summaryItem()}>
                      <span className={summaryLabel()}>{item.label}:</span>
                      <span className={summaryValue()}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Render CTA button (only shown if no completed instances) */}
      {completedInstances.length === 0 && (
        <div id={section.id} className={ctaContainer()}>
          <ButtonWrapper
            type="button"
            text={section.startCTAText}
            ctaComponentClass="default"
            ctaVariant="primary"
            onClick={() => onSectionClick(section.id)}
            disabled={!isCTAEnabled}
            className="w-fit"
          />
        </div>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['flex', 'flex-col', 'gap-spacing-spacing-16'],
    cardsContainer: ['flex', 'flex-row', 'flex-wrap', 'gap-spacing-spacing-24'],
    cardContainer: [
      'flex',
      'flex-col',
      'gap-spacing-spacing-8',
      'p-component-card-item-portrait-content-padding-x',
      'rounded-component-card-item-image-radius',
      'bg-Surface-surface-default',
      'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
    ],
    cardHeader: ['flex', 'flex-row', 'justify-end'],
    editButton: ['text-color-core-color-1-Navy', 'underline', 'hover:no-underline'],
    summaryContainer: ['flex', 'flex-col', 'gap-spacing-spacing-8'],
    summaryItem: ['flex', 'flex-col', 'gap-1'],
    summaryLabel: ['text-sm', 'font-semibold', 'text-color-core-color-1-Navy'],
    summaryValue: ['text-sm'],
    ctaContainer: [
      'w-full',
      'p-padding-base',
      'flex',
      'flex-row',
      'justify-center',
      'border',
      'border-dashed',
      'border-color-core-color-1-Navy',
    ],
  },
});
